import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FaClock, FaChartBar, FaTrophy, FaLightbulb, FaRedo, FaHome, FaCheck, FaHandPaper, FaSignal, FaPlus, FaArrowDown } from "react-icons/fa";
import { SkeletonSearching } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./EndInteraction.css";
export default function EndInteraction() {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = useParams();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [friendRequestSent, setFriendRequestSent] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
  const { reason, partner } = location.state || {};
  useEffect(() => {
    if (!chatId) return;
    // Fetch session data with EQ mistakes AND generate AI report
    const fetchSessionData = async () => {
      try {
        // Wait a bit for the session to be saved to DB
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const response = await fetch(`${API_URL}/api/funtellime/session/${chatId}`);
        const data = await response.json();
        console.log('[EndInteraction] Session data:', data);
        setSessionData(data);
        
        // Generate AI-powered report
        const reportResponse = await fetch(`${API_URL}/api/funtellime/session/${chatId}/generate-report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId })
        });
        
        if (reportResponse.ok) {
          const reportData = await reportResponse.json();
          console.log('[EndInteraction] AI Report:', reportData);
          setAiReport(reportData.report);
        }
        
      } catch (error) {
        console.error('[EndInteraction] Error fetching session data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSessionData();
  }, [chatId, userId]);
  const handleSendFriendRequest = async () => {
    if (!partner || !userId) return;
    try {
      // TODO: Implement friend request API
      const response = await fetch(`${API_URL}/api/friends/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromUserId: parseInt(userId),
          toUserId: partner.userId
        })
      });
      if (response.ok) {
        setFriendRequestSent(true);
        // Update database that friend request was sent
        await fetch(`${API_URL}/api/funtellime/session/${chatId}/friend-request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sent: true })
        });
      }
    } catch (error) {
      }
  };
  const handleStartNewChat = () => {
    navigate("/funtellime/text-lobby");
  };
  const handleSignOff = () => {
    navigate("/dashboard");
  };
  if (loading) {
    return (
      <div className="end-interaction-container">
        <SkeletonSearching />
      </div>
    );
  }
  // Count EQ mistakes made by current user
  const userMistakes = sessionData?.eqMistakes?.filter(
    m => m.userId === parseInt(userId)
  ) || [];
  const endReason = {
    'timeout': {
      icon: <FaClock />,
      title: 'Time\'s Up!',
      message: 'No response was received within 2 minutes.'
    },
    'manual': {
      icon: <FaHandPaper />,
      title: 'Chat Ended',
      message: 'You ended the conversation.'
    },
    'partner-left': {
      icon: <FaHandPaper />,
      title: 'Partner Left',
      message: 'Your partner has left the conversation.'
    },
    'partner-disconnected': {
      icon: <FaSignal />,
      title: 'Connection Lost',
      message: 'Your partner disconnected.'
    }
  };
  const reasonInfo = endReason[reason] || endReason['manual'];
  return (
    <div className="end-interaction-container">
      <div className="end-interaction-content">
        {/* Scroll hint */}
        <div className="scroll-hint"><FaArrowDown /> Scroll down for your EQ report</div>
        {/* Top Row: End Reason + Partner side by side */}
        <div className="top-cards-row">
          {/* End Reason */}
          <div className="end-reason-card">
            <span className="end-icon">{reasonInfo.icon}</span>
            <h2>{reasonInfo.title}</h2>
            <p>{reasonInfo.message}</p>
          </div>
          {/* Partner Card - Friend Request */}
          {partner && (
            <div className="partner-card">
              <div className="partner-avatar-large">
                <img
                  src={partner.profilePic || 'https://via.placeholder.com/100'}
                  alt={partner.name}
                />
              </div>
              <h3>{partner.name}</h3>
              <p className="partner-subtitle">Stay connected?</p>
              {!friendRequestSent ? (
                <button className="friend-request-btn" onClick={handleSendFriendRequest}>
                  <FaPlus /> Add Friend
                </button>
              ) : (
                <div className="friend-request-sent">
                  <span className="check-icon"><FaCheck /></span>
                  Request sent!
                </div>
              )}
            </div>
          )}
        </div>
        {/* EQ Mistakes Report */}
        <div className="eq-report-card">
          <h2><FaChartBar /> Your Emotional Intelligence Report</h2>
          
          {/* AI-Generated Summary */}
          {aiReport && (
            <div className="ai-report-summary">
              <div className="ai-badge">
                <FaLightbulb /> AI Analysis
              </div>
              <p className="ai-report-text">{aiReport}</p>
            </div>
          )}
          
          {userMistakes.length === 0 ? (
            <div className="no-mistakes">
              <span className="trophy-icon"><FaTrophy /></span>
              <h3>Excellent Communication!</h3>
              <p>No emotional intelligence mistakes detected in your messages.</p>
            </div>
          ) : (
            <div className="mistakes-list">
              <p className="mistakes-intro">
                We detected {userMistakes.length} area{userMistakes.length > 1 ? 's' : ''} for improvement:
              </p>
              {userMistakes.map((mistake, index) => (
                <div key={index} className="mistake-item">
                  <div className="mistake-header">
                    <span className="mistake-number">{index + 1}</span>
                    <h4>{mistake.mistake.title}</h4>
                  </div>
                  <p className="mistake-description">{mistake.mistake.description}</p>
                  {mistake.message && (
                    <div className="mistake-trigger">
                      Your message: "<em>{mistake.message}</em>"
                    </div>
                  )}
                  {mistake.confidence && (
                    <div className="confidence-badge">
                      AI Confidence: {mistake.confidence}%
                    </div>
                  )}
                </div>
              ))}
              <div className="improvement-tip">
                <span className="tip-icon"><FaLightbulb /></span>
                <p>
                  <strong>Growth Tip:</strong> These insights are opportunities to enhance your emotional 
                  intelligence. Practice being more aware of how your words might affect others.
                </p>
              </div>
            </div>
          )}
        </div>
        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="primary-btn" onClick={handleStartNewChat}>
            <FaRedo /> Start New Chat
          </button>
          <button className="secondary-btn" onClick={handleSignOff}>
            <FaHome /> Back to Dashboard
          </button>
        </div>
        {/* Stats */}
        <div className="chat-stats">
          <div className="stat-item">
            <span className="stat-value">{sessionData?.messages?.length || 0}</span>
            <span className="stat-label">Messages</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {Math.floor((sessionData?.endedAt - sessionData?.startTime) / 60000) || 0}m
            </span>
            <span className="stat-label">Duration</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{userMistakes.length}</span>
            <span className="stat-label">EQ Insights</span>
          </div>
        </div>
      </div>
    </div>
  );
}
